import React, {useState, ReactElement, useEffect} from "react";
import {
    Card, CardContent, Typography, Chip, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Box, Tooltip, Stack, Divider, Container, CircularProgress,
} from "@mui/material";
import {
    Visibility, Lock, LockOpen, Delete, CheckCircle,
    AccessTime, MoreVert,
} from "@mui/icons-material";
import {useFetch} from "./useFetch";
import {fetchShares} from "./api";
import {ShareStatus, ChipColor} from "./types";
import ComponentLoader from "./ComponentLoader";
import {ShareListDTO} from "../../types/DTOs/ShareListDTO";
import {DashboardAPI} from "../../services/API/DashboardAPI";
import {ShareListDashboardDTO} from "../../types/DTOs/Dashboard/ShareListDashboardDTO";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isExpired = (date: Date | null | undefined): boolean => {
    if (date === null || date === undefined) {
        return false;
    }
    return new Date(date) < new Date();
};

const getShareStatus = (share: ShareListDTO): ShareStatus => {
    if (share.isDeleted) return {label: "Eliminata", color: "error" as ChipColor, icon: <Delete sx={{fontSize: 14}}/>};
    if (isExpired(share.expirationDate)) return {
        label: "Scaduta",
        color: "warning" as ChipColor,
        icon: <AccessTime sx={{fontSize: 14}}/>
    };
    return {label: "Attiva", color: "success" as ChipColor, icon: <CheckCircle sx={{fontSize: 14}}/>};
};

const formatDate = (date: Date): string =>
    new Date(date).toLocaleDateString("it-IT", {day: "2-digit", month: "short", year: "numeric"});

const truncateToken = (token: string): string => token.slice(0, 12) + "…";

// ─── Component ────────────────────────────────────────────────────────────────
export default function SharesTable() {
    const [hoveredRow, setHoveredRow] = useState<string | null>(null);
    //const { data, loading, error, refetch } = useFetch(fetchShares);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<ShareListDashboardDTO[]>([]);
    const [shares, setShares] = useState<ShareListDashboardDTO[]>([]);

    //const shares = data ?? [];
    const activeCount = 0;
    const totalViews = 0;

    useEffect(() => {
        loadData();
    }, [])

    useEffect(() => {
        setShares(data);
    }, [data]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        // caricamento dati
        const dataReturn = await DashboardAPI.Instance<DashboardAPI>().getSharesList();
        setData(dataReturn);

        setLoading(false);
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{mt: 4}}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    return (
        <Card elevation={0} sx={{border: "1px solid", borderColor: "divider", borderRadius: 3}}>
            <CardContent sx={{pb: 0}}>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                    Link di Condivisione
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Gestione completa di tutti i link generati
                </Typography>
            </CardContent>

            <ComponentLoader loading={loading} error={error} skeletonHeight={240}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{
                                "& th": {
                                    bgcolor: "action.hover",
                                    fontWeight: 700,
                                    fontSize: 12,
                                    letterSpacing: 0.5,
                                    textTransform: "uppercase",
                                    color: "text.secondary",
                                    borderBottom: "1px solid",
                                    borderColor: "divider"
                                }
                            }}>
                                <TableCell>Token</TableCell>
                                <TableCell>Versione CV</TableCell>
                                <TableCell>Stato</TableCell>
                                {/*<TableCell>Password</TableCell>*/}
                                <TableCell align="center">Visualizzazioni</TableCell>
                                <TableCell>Scadenza</TableCell>
                                <TableCell>Creato il</TableCell>
                                <TableCell align="right">Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shares.map((share) => {
                                const status = getShareStatus(share);
                                return (
                                    <TableRow
                                        key={share.id}
                                        onMouseEnter={() => setHoveredRow(share.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        sx={{
                                            opacity: share.isDeleted ? 0.5 : 1,
                                            bgcolor: hoveredRow === share.id ? "action.hover" : "transparent",
                                            transition: "background 0.1s",
                                            "& td": {borderColor: "divider", py: 1.5},
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace" fontWeight={600}
                                                        color="primary.main" fontSize={12}>
                                                {truncateToken(share.shareToken)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={share.cvVersionId} size="small" variant="outlined"
                                                  sx={{fontSize: 11, borderRadius: 1}}/>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={status.icon as ReactElement}
                                                label={status.label}
                                                color={status.color}
                                                size="small"
                                                sx={{fontWeight: 600, fontSize: 11}}
                                            />
                                        </TableCell>
                                        {/*<TableCell>
                                            <Tooltip title={share.password || "Nessuna password"}>
                                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: share.password ? "text.primary" : "text.disabled" }}>
                                                    {share.password ? <Lock sx={{ fontSize: 14 }} /> : <LockOpen sx={{ fontSize: 14 }} />}
                                                    <Typography variant="caption">
                                                        {share.password ? "•••••" : "—"}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>*/}
                                        <TableCell align="center">
                                            <Stack direction="row" alignItems="center" justifyContent="center"
                                                   spacing={0.5}>
                                                <Visibility sx={{fontSize: 13, color: "text.secondary"}}/>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {share.viewCount}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color={isExpired(share.expirationDate) && !share.isDeleted ? "warning.main" : "text.secondary"}
                                                fontWeight={isExpired(share.expirationDate) ? 600 : 400}
                                            >
                                                {share.expirationDate === null || share.expirationDate === undefined ? '-' : formatDate(share.expirationDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(share.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" disabled={share.isDeleted}>
                                                <MoreVert sx={{fontSize: 16}}/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ComponentLoader>

            <Divider/>
            <Box px={2} py={1.5}>
                <Typography variant="caption" color="text.secondary">
                    {shares.length} share totali · {activeCount} attive · {totalViews} visualizzazioni aggregate
                </Typography>
            </Box>
        </Card>
    );
}